import { SessionKey } from "../util/Constant";

/**
 * Persists BO theme preview id until the user clicks "Exit preview".
 * Synced on full page load in index.js (see applyThemePreviewBootstrapSync).
 */
export function getThemePreviewGuidFromStorage() {
  if (typeof window === "undefined") return null;
  try {
    const v = localStorage.getItem(SessionKey._THEME_PREVIEW_GUID);
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

export function setThemePreviewGuidInStorage(guid) {
  if (typeof window === "undefined" || !guid) return;
  try {
    localStorage.setItem(SessionKey._THEME_PREVIEW_GUID, String(guid).trim());
  } catch {
    /* private mode / quota */
  }
}

export function clearThemePreviewGuidStorage() {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(SessionKey._THEME_PREVIEW_GUID);
  } catch {
    /* private mode */
  }
}

/**
 * Run before ReactDOM.render.
 * - Server confirmed preview → save to localStorage.
 * - URL has ?preview= but server did not → invalid/expired; clear storage.
 * - Stored preview but URL missing ?preview → redirect so SSR can apply theme on refresh.
 */
export function applyThemePreviewBootstrapSync() {
  if (typeof window === "undefined") return;

  const serverGuid = window.__THEME_PREVIEW_GUID__ || null;
  const url = new URL(window.location.href);
  const hasPreviewParam = url.searchParams.has("preview");

  if (serverGuid) {
    setThemePreviewGuidInStorage(serverGuid);
    return;
  }

  if (hasPreviewParam) {
    clearThemePreviewGuidStorage();
    return;
  }

  const stored = getThemePreviewGuidFromStorage();
  if (stored) {
    url.searchParams.set("preview", stored);
    window.location.replace(url.toString());
  }
}

/**
 * Keep `?preview=<guid>` on the address bar while preview mode is active.
 * Call after client-side navigation (Router does not replay bootstrap; links often drop the query).
 * Uses history.replace so back/forward and shared URLs stay consistent without extra history entries.
 *
 * @param {{ replace: (path: string) => void }} history - react-router history
 * @param {{ pathname: string, search: string, hash: string }} location
 * @param {string|null|undefined} guid
 */
export function syncThemePreviewSearchParam(history, location, guid) {
  if (!guid || typeof window === "undefined" || !history?.replace) return;

  const params = new URLSearchParams(location.search || "");
  if (params.get("preview") === guid) return;

  params.set("preview", guid);
  const q = params.toString();
  const next =
    location.pathname +
    (q ? `?${q}` : "") +
    (location.hash || "");
  history.replace(next);
}
